import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Filter, SlidersHorizontal, X } from 'lucide-react';
import { products } from '../data/products';
import { categories } from '../data/categories';
import { ProductCard } from '../components/ProductCard';
export function Shop() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q')?.toLowerCase();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<number>(150);
  const [showOnSaleOnly, setShowOnSaleOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'featured' | 'price-asc' | 'price-desc'>('featured');
  const filteredProducts = useMemo(() => {
    let result = [...products];
    // Category filter
    if (category) {
      result = result.filter(p => p.category === category);
    }
    // Search filter
    if (searchQuery) {
      result = result.filter(p => p.name.toLowerCase().includes(searchQuery) || p.description.toLowerCase().includes(searchQuery));
    }
    // Price filter
    result = result.filter(p => (p.salePrice || p.price) <= priceRange);
    // Sale filter
    if (showOnSaleOnly) {
      result = result.filter(p => !!p.salePrice);
    }
    // Sorting
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
        break;
      case 'featured':
      default:
        result.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
        break;
    }
    return result;
  }, [category, searchQuery, priceRange, showOnSaleOnly, sortBy]);
  const categoryName = category ? categories.find(c => c.id === category)?.name : 'All Products';
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-612">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4" data-id="element-613">
        <div data-id="element-614">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-615">
            {searchQuery ? `Search results for "${searchQuery}"` : categoryName}
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-616">
            {filteredProducts.length} products
          </p>
        </div>

        <div className="flex items-center gap-4" data-id="element-617">
          <button onClick={() => setIsMobileFiltersOpen(true)} className="md:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white" data-id="element-618">
            <Filter className="w-4 h-4" data-id="element-619" /> Filters
          </button>

          <div className="flex items-center gap-2" data-id="element-620">
            <label htmlFor="sort" className="text-sm text-gray-500 hidden sm:block" data-id="element-621">
              Sort by:
            </label>
            <select id="sort" value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="border-gray-300 rounded-lg text-sm focus:ring-red-500 focus:border-red-500 py-2 pl-3 pr-10 bg-white border" data-id="element-622">
              <option value="featured" data-id="element-623">Featured</option>
              <option value="price-asc" data-id="element-624">Price: Low to High</option>
              <option value="price-desc" data-id="element-625">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8" data-id="element-626">
        {/* Desktop Sidebar Filters */}
        <div className="hidden md:block w-64 flex-shrink-0" data-id="element-627">
          <div className="sticky top-24 space-y-8" data-id="element-628">
            <div data-id="element-629">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-630">
                <SlidersHorizontal className="w-4 h-4" data-id="element-631" /> Filters
              </h3>

              {/* Categories */}
              <div className="mb-6" data-id="element-632">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-633">
                  Categories
                </h4>
                <ul className="space-y-2" data-id="element-634">
                  <li data-id="element-635">
                    <a href="/shop" className={`text-sm ${!category ? 'text-red-600 font-medium' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-636">
                      All Products
                    </a>
                  </li>
                  {categories.map(cat => <li key={cat.id} data-id="element-637">
                      <a href={`/shop/${cat.id}`} className={`text-sm ${category === cat.id ? 'text-red-600 font-medium' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-638">
                        {cat.name}
                      </a>
                    </li>)}
                </ul>
              </div>

              {/* Price Range */}
              <div className="mb-6" data-id="element-639">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-640">
                  Max Price: MVR {priceRange}
                </h4>
                <input type="range" min="0" max="200" step="10" value={priceRange} onChange={e => setPriceRange(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600" data-id="element-641" />
                <div className="flex justify-between text-xs text-gray-500 mt-2" data-id="element-642">
                  <span data-id="element-643">MVR 0</span>
                  <span data-id="element-644">MVR 200+</span>
                </div>
              </div>

              {/* Toggles */}
              <div data-id="element-645">
                <label className="flex items-center gap-3 cursor-pointer" data-id="element-646">
                  <input type="checkbox" checked={showOnSaleOnly} onChange={e => setShowOnSaleOnly(e.target.checked)} className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500" data-id="element-647" />
                  <span className="text-sm text-gray-700" data-id="element-648">On Sale Only</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filters Modal */}
        {isMobileFiltersOpen && <div className="fixed inset-0 z-50 md:hidden flex" data-id="element-649">
            <div className="fixed inset-0 bg-black/40" onClick={() => setIsMobileFiltersOpen(false)} data-id="element-650" />
            <div className="relative w-full max-w-xs bg-white h-full shadow-xl flex flex-col" data-id="element-651">
              <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-652">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-653">Filters</h2>
                <button onClick={() => setIsMobileFiltersOpen(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-654">
                  <X className="w-5 h-5" data-id="element-655" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-6" data-id="element-656">
                {/* Mobile Categories */}
                <div data-id="element-657">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-658">
                    Categories
                  </h4>
                  <ul className="space-y-3" data-id="element-659">
                    <li data-id="element-660">
                      <a href="/shop" className={`block text-sm ${!category ? 'text-red-600 font-medium' : 'text-gray-600'}`} data-id="element-661">
                        All Products
                      </a>
                    </li>
                    {categories.map(cat => <li key={cat.id} data-id="element-662">
                        <a href={`/shop/${cat.id}`} className={`block text-sm ${category === cat.id ? 'text-red-600 font-medium' : 'text-gray-600'}`} data-id="element-663">
                          {cat.name}
                        </a>
                      </li>)}
                  </ul>
                </div>
                {/* Mobile Price */}
                <div data-id="element-664">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-665">
                    Max Price: MVR {priceRange}
                  </h4>
                  <input type="range" min="0" max="200" step="10" value={priceRange} onChange={e => setPriceRange(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600" data-id="element-666" />
                </div>
                {/* Mobile Toggles */}
                <div data-id="element-667">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-668">
                    <input type="checkbox" checked={showOnSaleOnly} onChange={e => setShowOnSaleOnly(e.target.checked)} className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500" data-id="element-669" />
                    <span className="text-sm text-gray-700" data-id="element-670">On Sale Only</span>
                  </label>
                </div>
              </div>
              <div className="p-4 border-t border-gray-100" data-id="element-671">
                <button onClick={() => setIsMobileFiltersOpen(false)} className="w-full bg-red-600 text-white py-3 rounded-lg font-medium" data-id="element-672">
                  Apply Filters
                </button>
              </div>
            </div>
          </div>}

        {/* Product Grid */}
        <div className="flex-1" data-id="element-673">
          {filteredProducts.length === 0 ? <div className="text-center py-12" data-id="element-674">
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-675">
                No products found
              </h3>
              <p className="text-gray-500" data-id="element-676">
                Try adjusting your filters or search query.
              </p>
              <button onClick={() => {
            setPriceRange(200);
            setShowOnSaleOnly(false);
          }} className="mt-4 text-red-600 font-medium hover:underline" data-id="element-677">
                Clear all filters
              </button>
            </div> : <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-678">
              {filteredProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-679" />)}
            </div>}
        </div>
      </div>
    </div>;
}